import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatAmount, formatDayTime, formatPhone, withCountRu } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, failureText } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { useSaveStore, useStores, type Store } from './api'
import { ALL_DAY, filterStores, hoursText, storeTitle } from './model'

export interface StoresScreenProps {
  onToast: (message: string) => void
}

/** Аптеки сети: где работают, кто заведует и открыты ли прямо сейчас. */
export function StoresScreen({ onToast }: StoresScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const stores = useStores()
  const save = useSaveStore()

  const canUpdate = can('stores', 'u')

  function toggle(store: Store, active: boolean) {
    save.mutate(
      { ...store, active },
      {
        onSuccess: () => {
          onToast(
            active
              ? `«${storeTitle(store)}» снова работает`
              : `«${storeTitle(store)}» закрыта: самовывоз и доставка из неё недоступны`,
          )
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  const list = stores.data ? filterStores(stores.data, q) : []

  return (
    <>
      <PageActions>
        {stores.data ? (
          <span
            className="ui-count adm-count"
            aria-label={withCountRu(stores.data.length, ['аптека', 'аптеки', 'аптек'])}
          >
            {stores.data.length}
          </span>
        ) : null}
        <span className="spacer" />
        {can('stores', 'c') ? (
          <button
            type="button"
            className="ui-btn"
            onClick={() => {
              void navigate('/stores/new')
            }}
          >
            <Icon name="i-plus" />
            Добавить аптеку
          </button>
        ) : null}
      </PageActions>

      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Район, адрес или заведующий"
            aria-label="Поиск аптеки"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
            }}
          />
        </div>
      </div>

      <section className="ui-table-card" aria-label="Аптеки">
        {stores.isPending ? <TableSkeleton what="аптеки" /> : null}
        {stores.isError ? (
          <Failed
            what="аптеки"
            error={stores.error}
            onRetry={() => {
              void stores.refetch()
            }}
          />
        ) : null}

        {stores.data ? (
          <div className="ui-table-wrap">
            <table className="ui-table ui-stores">
              <colgroup>
                <col />
                <col className="w-hours" />
                <col className="w-manager" />
                <col className="w-orders" />
                <col className="w-methods" />
                <col className="w-sync" />
                <col className="w-on" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Аптека</th>
                  <th scope="col">Режим</th>
                  <th scope="col">Контакты</th>
                  <th scope="col" data-align="end">
                    Заказов
                  </th>
                  <th scope="col">Получение</th>
                  <th scope="col">Синхронизация</th>
                  <th scope="col" data-align="center">
                    Статус
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      {stores.data.length === 0 ? (
                        <div className="ui-empty">
                          <Icon name="i-pin" />
                          <b>Аптек пока нет</b>
                        </div>
                      ) : (
                        <div className="ui-empty">
                          <Icon name="i-search" />
                          <b>Аптеки не найдены</b>
                          <p>Измените запрос или сбросьте поиск.</p>
                          <button
                            type="button"
                            className="ui-btn"
                            data-size="sm"
                            data-variant="secondary"
                            onClick={() => {
                              setQ('')
                            }}
                          >
                            Сбросить поиск
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : null}
                {list.map((store) => {
                  const href = `/stores/${store.id}`
                  const hours = hoursText(store)
                  return (
                    <tr
                      key={store.id}
                      data-href={href}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td>
                        <Link
                          to={href}
                          className="primary"
                          onClick={(event) => {
                            event.stopPropagation()
                          }}
                        >
                          {storeTitle(store)}
                        </Link>
                        <span className="secondary">
                          {[store.district, store.address].filter(Boolean).join(' · ')}
                        </span>
                      </td>
                      <td>
                        <span className="hours">
                          {hours === ALL_DAY ? (
                            <span className="ui-tag">24/7</span>
                          ) : (
                            <span className="num">{hours}</span>
                          )}
                          {/* Открыта ли сейчас — меняется в течение дня, поэтому статус,
                              а не метка. */}
                          {store.openNow ? (
                            <span className="ui-status" data-tone="success">
                              открыта
                            </span>
                          ) : (
                            <span className="ui-status">закрыта</span>
                          )}
                        </span>
                      </td>
                      {/* Кто заведует и как дозвониться — одна ячейка «контакты». */}
                      <td>
                        <span className="st-contact">
                          <span>{store.manager || '—'}</span>
                          {store.phone ? (
                            <span className="secondary num">{formatPhone(store.phone)}</span>
                          ) : null}
                        </span>
                      </td>
                      {/* Счётчик считает сервер для списка: тысячи — с разрядами. */}
                      <td data-align="end" className="num">
                        {formatAmount(store.ordersCount)}
                      </td>
                      <td>
                        {/* Способы получения — свойства аптеки, не состояние: метки
                            в одну строку, чтобы строки были одной высоты. */}
                        <span className="methods">
                          {store.acceptsPickup ? <span className="ui-tag">самовывоз</span> : null}
                          {store.acceptsDelivery ? <span className="ui-tag">доставка</span> : null}
                          {store.dispensesRx ? (
                            <span className="ui-tag" data-tone="rx">
                              рецепт
                            </span>
                          ) : null}
                        </span>
                      </td>
                      {/* Когда FOM последний раз прислал остатки. Нет FOM или файлов
                          ещё не было — так и пишем. */}
                      <td>
                        {store.fomSyncedAt ? (
                          <span className="num">{formatDayTime(store.fomSyncedAt)}</span>
                        ) : (
                          <span className="ui-status">нет данных</span>
                        )}
                      </td>
                      <td
                        data-align="center"
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        {canUpdate ? (
                          <label className="ui-switch" data-size="sm">
                            <input
                              type="checkbox"
                              role="switch"
                              checked={store.active}
                              aria-label={`Аптека «${storeTitle(store)}» работает`}
                              onChange={(e) => {
                                toggle(store, e.target.checked)
                              }}
                            />
                          </label>
                        ) : store.active ? (
                          <span className="ui-status" data-tone="success">
                            работает
                          </span>
                        ) : (
                          // «закрыта» уже занято часами работы — выключенная аптека
                          // подписана иначе, чтобы их не спутать.
                          <span className="ui-status">отключена</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  )
}
